import { PrimaryGoal } from 'src/types/inputs';
import { Input } from 'src/lib/constants';
import { InputsReducer } from 'src/redux/reducers/inputs';
import { ExperimentsReducer } from 'src/redux/reducers/experiments';

type ScreenLabel =
  | string
  | ((inputs: InputsReducer, experiments?: ExperimentsReducer) => string);
type ScreenLabelsLookup = Record<Input, ScreenLabel>;
const SCREEN_LABELS: ScreenLabelsLookup = {
  [Input.AirlineChoice]: 'Which airline do you prefer?',
  [Input.AnnualFees]: 'Are you willing to pay an annual fee?',
  [Input.CardDeposit]: 'Are you willing to pay a minimum deposit?',
  [Input.CollegeStudent]: 'Are you a full-time college student?',
  [Input.IsBuildingCredit]:
    'Great! Are you building your credit score from scratch?',
  [Input.CreditScore]:
    "Let's start with your credit. What is your score these days?",
  [Input.CurrentBalanceCompany]:
    'Which bank holds the debt you want to transfer?',
  [Input.HotelChoice]: 'Which hotel chain do you prefer?',
  [Input.HowLongToPayOffDebt]: (inputs) =>
    `${
      inputs?.[Input.PrimaryGoal] === PrimaryGoal.Debt ? '' : 'Great! '
    }How long would you need to pay off this balance?`,
  [Input.InternationalTravel]: 'Do you plan to travel internationally?',
  [Input.IsDebtCreditCard]:
    'Are you looking to pay down a credit card balance?',
  [Input.LuxuryPerks]: 'Which travel perks are you most interested in?',
  [Input.PrimaryGoal]: 'Why are you looking for a new credit card?',
  [Input.RewardsAndPerks]:
    'Which is most appealing to you in a travel credit card?',
  [Input.SpecificAirlineOrHotel]:
    'Do you have a specific airline or hotel chain preference?',
  [Input.SpendingCategories]:
    'Which spend categories do you want to earn bonus rewards in?',
  [Input.SecurityDeposit]: 'How much security deposit can you afford?',
  [Input.CashBackPreference]:
    'Which is more appealing to you in a credit cards rewards program?',
  [Input.CashBackBigPurchase]: 'Are you planning to make a big purchase soon?',
  [Input.ManagingCategories]:
    'How do you feel about managing spending categories?',
  // TODO: Update label for Build Credit Student flow
  [Input.MonthlySpend]:
    'Roughly how much do you plan to spend per month with this card?',
  [Input.TypeOfDebt]: 'What type of debt do you want to transfer?',
  [Input.IntroAPROrRewards]: 'Which is more important to you?',
  [Input.PurchaseSpend]: 'How much do you plan to spend on this purchase?',
  [Input.StudentSpendingCategories]:
    'Which categories best reflect your spending?',
  [Input.FullDebt]: 'What debt(s) do you want to transfer?',
  [Input.PurchaseSpendingCategories]:
    'Which spend categories best match your planned purchase(s)?',
};

const QUICK_EDIT_LABELS: Partial<ScreenLabelsLookup> = {
  [Input.IsBuildingCredit]: 'Are you building your credit score from scratch?',
  [Input.HowLongToPayOffDebt]:
    'How long would you need to pay off this balance?',
  [Input.IsDebtCreditCard]: 'Is this debt a credit card balance?',
  [Input.MonthlySpend]: 'How much do you plan to spend per month?',
  [Input.PurchaseSpend]: 'How much do you plan to spend on this purchase?',
};

const getLabelValue = (
  label: ScreenLabel | undefined,
  inputs: InputsReducer,
  experiments?: ExperimentsReducer
) => {
  if (typeof label === 'string') {
    return label;
  }
  return typeof label === 'function' ? label(inputs, experiments) : '';
};

export const getInputLabel = (
  input: Input,
  inputs: InputsReducer,
  experiments?: ExperimentsReducer,
  isQuickEdit: boolean = false
) => {
  if (isQuickEdit && QUICK_EDIT_LABELS[input] !== undefined) {
    return getLabelValue(QUICK_EDIT_LABELS[input], inputs, experiments);
  }

  return getLabelValue(SCREEN_LABELS[input], inputs, experiments);
};
